/**
 * Copy Formatters - Convert agent outputs to formatted text for clipboard
 */

import { formatLinkedInCopy } from './linkedinFormatter';
import { formatTwitterCopy } from './twitterFormatter';
import { formatNewsletterCopy } from './newsletterFormatter';
import { formatPressReleaseCopy } from './pressReleaseFormatter';
import { formatMarketingStrategistCopy } from './marketingStrategistFormatter';
import { formatSEOKeywordCopy } from './seoKeywordFormatter';
import { formatSEOContentCopy } from './seoContentFormatter';

/**
 * Type definition for copy formatter functions
 */
type CopyFormatter = (data: any) => string;

/**
 * Registry of copy formatters by agent type
 */
const COPY_FORMATTERS: Record<string, CopyFormatter> = {
  linkedin_agent: formatLinkedInCopy,
  twitter_agent: formatTwitterCopy,
  newsletter_agent: formatNewsletterCopy,
  press_release_agent: formatPressReleaseCopy,
  marketing_strategist_agent: formatMarketingStrategistCopy,
  seo_keyword_agent: formatSEOKeywordCopy,
  seo_content_agent: formatSEOContentCopy,
};

/**
 * Get formatted text for copying based on agent type
 *
 * @param agentType - The type of agent that generated the content
 * @param agentOutput - The output data from the agent
 * @returns Formatted text ready for clipboard
 */
export const getCopyText = (agentType: string | undefined, agentOutput: any): string => {
  // If no agent type or output, return empty or JSON
  if (!agentOutput) {
    return '';
  }

  // If no agent type specified, try to format as JSON
  if (!agentType) {
    return JSON.stringify(agentOutput, null, 2);
  }

  // Get the appropriate formatter
  const formatter = COPY_FORMATTERS[agentType];

  // If no formatter found, fall back to JSON
  if (!formatter) {
    console.warn(`No copy formatter found for agent type: ${agentType}`);
    return JSON.stringify(agentOutput, null, 2);
  }

  // Use the formatter
  try {
    return formatter(agentOutput);
  } catch (error) {
    console.error(`Error formatting copy for ${agentType}:`, error);
    // Fall back to JSON on error
    return JSON.stringify(agentOutput, null, 2);
  }
};

// Export individual formatters for testing or direct use
export {
  formatLinkedInCopy,
  formatTwitterCopy,
  formatNewsletterCopy,
  formatPressReleaseCopy,
  formatMarketingStrategistCopy,
  formatSEOKeywordCopy,
  formatSEOContentCopy,
};
